// Epoch 23 v2: Trial Error Boundary - DEFENSIVE ERROR ISOLATION
// Version: 2.1 (Enhanced with Opus recommendations)
// Date: August 4, 2025
// CRITICAL: Isolates trial component failures to prevent app-wide crashes

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  componentName?: string; // For better error tracking
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorCount: number;
}

/**
 * DEFENSIVE: Error Boundary specifically for trial features
 * Prevents trial component errors from crashing the entire application
 * This is a CRITICAL defense against v1-style cascade failures
 */
class TrialErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state to render fallback UI on next render
    console.error('🚨 Trial Error Boundary caught error:', error.message);
    return { 
      hasError: true, 
      error 
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, componentName } = this.props;
    const errorCount = this.state.errorCount + 1;

    // Log error details for debugging
    console.error('🔥 Trial component error details:', {
      component: componentName || 'Unknown',
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorCount
    });

    // Update error count
    this.setState({ errorCount });

    // Call optional error handler
    if (onError) {
      onError(error, errorInfo);
    }

    // Report to analytics if available (but don't fail if not)
    try {
      if (typeof window !== 'undefined' && (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag) {
        (window as unknown as { gtag: (...args: unknown[]) => void }).gtag('event', 'trial_component_error', {
          event_category: 'trial_system',
          event_label: componentName || 'unknown',
          value: errorCount,
          error_message: error.message
        });
      }
    } catch (analyticsError) {
      console.warn('Failed to report error to analytics:', analyticsError);
    }

    // OPUS ENHANCEMENT: Auto-recovery mechanism
    // If error persists, give up after 3 attempts to prevent infinite loops
    if (errorCount < 3) {
      console.log(`🔄 Attempting auto-recovery (attempt ${errorCount}/3)`);
      
      // Clear any existing timeout
      if (this.resetTimeoutId) {
        clearTimeout(this.resetTimeoutId);
      }

      // Try to recover after a delay (exponential backoff)
      const delay = Math.min(1000 * Math.pow(2, errorCount - 1), 5000);
      this.resetTimeoutId = setTimeout(() => {
        console.log('🔧 Attempting to reset error boundary');
        this.setState({ hasError: false, error: null });
      }, delay);
    } else {
      console.error('❌ Trial component failed after 3 attempts, giving up');
    }
  }

  componentWillUnmount() {
    // Clean up timeout on unmount
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  handleReset = () => {
    console.log('👤 User manually resetting error boundary');
    this.setState({ 
      hasError: false, 
      error: null, 
      errorCount: 0 
    });
  };

  render() {
    const { hasError, error, errorCount } = this.state;
    const { fallback, children, componentName } = this.props;

    if (hasError) {
      // Custom fallback UI if provided
      if (fallback) {
        return <>{fallback}</>;
      }

      // Default fallback UI with recovery options
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 m-2">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg 
                className="h-5 w-5 text-yellow-400" 
                viewBox="0 0 20 20" 
                fill="currentColor"
                aria-hidden="true"
              >
                <path 
                  fillRule="evenodd" 
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
                  clipRule="evenodd" 
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">
                Trial Feature Temporarily Unavailable
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  {errorCount >= 3 
                    ? 'This trial feature is experiencing issues and has been disabled.'
                    : 'A trial system component encountered an error. The main application continues to work normally.'
                  }
                </p>
                {componentName && (
                  <p className="mt-1 text-xs text-yellow-600">
                    Component: {componentName}
                  </p>
                )}
                {process.env.NODE_ENV === 'development' && error && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-yellow-600 hover:text-yellow-800">
                      Error Details (Development Only)
                    </summary>
                    <pre className="mt-1 text-xs bg-yellow-100 p-2 rounded overflow-auto">
                      {error.message}
                      {'\n'}
                      {error.stack}
                    </pre>
                  </details>
                )}
              </div>
              {errorCount < 3 && (
                <div className="mt-4">
                  <button
                    type="button"
                    className="bg-yellow-100 px-3 py-1.5 rounded-md text-sm font-medium text-yellow-800 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                    onClick={this.handleReset}
                  >
                    Try Again
                  </button>
                  <span className="ml-2 text-xs text-yellow-600">
                    Attempt {errorCount}/3
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return <>{children}</>;
  }
}

export default TrialErrorBoundary;